import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';
import { toast } from 'react-toastify';
import { GoogleLogin } from '@react-oauth/google';

const EyeIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
        <circle cx="12" cy="12" r="3" />
    </svg>
);

const EyeOffIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-6.5 0-10-7-10-7a18.5 18.5 0 0 1 5.06-5.94" />
        <path d="M9.9 4.24A10.9 10.9 0 0 1 12 5c6.5 0 10 7 10 7a18.5 18.5 0 0 1-2.16 3.19" />
        <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
        <line x1="2" y1="2" x2="22" y2="22" />
    </svg>
);

function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [unverifiedEmail, setUnverifiedEmail] = useState('');
    const [resending, setResending] = useState(false);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    // Helper function to handle successful login routing
    const handleSuccessfulLogin = async (token, role, userEmail) => {
        localStorage.setItem('token', token);
        localStorage.setItem('role', role);
        localStorage.setItem('email', userEmail);

        toast.success('Login successful!');

        if (role === 'WORKER') {
            navigate('/worker-dashboard');
        } else if (role === 'BUSINESS') {
            try {
                await axios.get(`${API_URL}/api/business/profile`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                navigate('/business-dashboard');
            } catch (profileErr) {
                navigate('/business/onboarding');
            }
        } else if (role === 'ADMIN') {
            navigate('/admin-dashboard');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const response = await axios.post(`${API_URL}/auth/login`, {
                email,
                password
            });

            await handleSuccessfulLogin(response.data.token, response.data.role, email);

        } catch (err) {
            const errorMsg = err.response?.data?.message || 'Login failed. Please try again.';
            if (errorMsg.toLowerCase().includes('verify')) {
                setUnverifiedEmail(email);
                toast.error('Please verify your email to continue.');
            } else {
                toast.error(errorMsg);
            }
        }
        setLoading(false);
    };

    const handleResendVerification = async () => {
        setResending(true);
        try {
            await axios.post(`${API_URL}/auth/resend-verification`, { email: unverifiedEmail });
            toast.success('Verification email sent! Check your inbox.');
            setUnverifiedEmail('');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to resend.');
        }
        setResending(false);
    };

    // GOOGLE LOGIN SUCCESS HANDLER
    const onGoogleSuccess = async (credentialResponse) => {
        setLoading(true);
        try {
            const response = await axios.post(`${API_URL}/auth/google`, {
                idToken: credentialResponse.credential // This is the ID Token (JWT)
            });

            await handleSuccessfulLogin(
                response.data.token, 
                response.data.role, 
                response.data.email || 'Google User'
            );

        } catch (err) {
            toast.error(err.response?.data?.message || 'Google sign-in failed.');
        }
        setLoading(false);
    };

    return (
        <div className="auth-page">
            <div className="auth-card">
                <div className="auth-brand fade-up delay-1">
                    <h1 className="auth-logo">infloir</h1>
                    <p className="auth-tagline">Global Task Marketplace</p>
                </div>

                <div className="fade-up delay-2">
                    <h2 className="auth-title">Welcome back</h2>
                    <p className="auth-subtitle">Sign in to your account</p>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="form-field fade-up delay-3">
                        <label className="form-label" htmlFor="email">Email</label>
                        <input
                            id="email"
                            type="email"
                            placeholder="you@example.com"
                            autoComplete="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                    </div>

                    <div className="form-field fade-up delay-4">
                        <div className="form-label-row">
                            <label className="form-label" htmlFor="password">Password</label>
                            <a href="/forgot-password" className="text-link">Forgot password?</a>
                        </div>
                        <div className="input-wrap">
                            <input
                                id="password"
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Enter your password"
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                            <button
                                type="button"
                                className="icon-btn"
                                onClick={() => setShowPassword(!showPassword)}
                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                            >
                                {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                            </button>
                        </div>
                    </div>

                    {unverifiedEmail && (
                        <div className="notice">
                            <p className="notice-text">Email not verified</p>
                            <button
                                type="button"
                                className="notice-btn"
                                onClick={handleResendVerification}
                                disabled={resending}
                            >
                                {resending ? 'Sending...' : 'Resend verification email'}
                            </button>
                        </div>
                    )}

                    <div className="fade-up delay-5">
                        <button type="submit" className="btn-primary auth-submit" disabled={loading}>
                            {loading && <span className="spinner" />}
                            {loading ? 'Signing in...' : 'Sign in'}
                        </button>
                    </div>
                </form>

                <div className="fade-up delay-6">
                    <div className="auth-divider">or</div>

                    {/* CUSTOM GOOGLE BUTTON OVERLAY */}
                    <div style={{ position: 'relative', width: '100%', marginBottom: '24px' }}>
                        
                        {/* Invisible Google Component that handles the actual OAuth logic */}
                        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, zIndex: 10 }}>
                            <GoogleLogin
                                onSuccess={onGoogleSuccess}
                                onError={() => toast.error('Google Sign-in was cancelled or failed.')}
                                width="100%"
                            />
                        </div>

                        {/* Our Beautiful Custom Button (Click passes through to Google) */}
                        <button 
                            type="button" 
                            className="btn-secondary" 
                            disabled={loading}
                            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '12px', pointerEvents: 'none' }}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                            </svg>
                            Continue with Google
                        </button>
                    </div>

                    <p className="auth-footer-text">
                        Don't have an account? <a href="/register">Create one</a>
                    </p>
                </div>
            </div>
        </div>
    );
}

export default Login;